import * as THREE from 'three';

/**
 * PhysicsAdapter — the player's single door onto `ctx.physics`.
 *
 * The physics team owns the real implementation (BVH capsule sweeps). This
 * adapter:
 *   1. normalises the results of `raycast` (three.js `Intersection` and the
 *      frozen contract shape both come out as `{point, normal, distance,
 *      object, surface}`),
 *   2. probes `capsuleMove` at boot and detects a missing / naive / throwing
 *      implementation,
 *   3. provides a complete local fallback (AABB collide-and-slide with
 *      step-up, ground snap and depenetration) so the player team is never
 *      blocked,
 *   4. keeps a last-known-safe position so the player can never be left
 *      inside geometry or fall out of the world.
 *
 * Nothing here mutates anyone else's state.
 */

const _v = new THREE.Vector3();
const _v2 = new THREE.Vector3();
const _v3 = new THREE.Vector3();
const _n = new THREE.Vector3();
const _sphere = new THREE.Vector3();
const _push = new THREE.Vector3();
const _gn = new THREE.Vector3();
const _up = new THREE.Vector3(0, 1, 0);
const _mat3 = new THREE.Matrix3();
const _box = new THREE.Box3();
const _size = new THREE.Vector3();

const clamp = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v);
const finite3 = (p) => Number.isFinite(p.x) && Number.isFinite(p.y) && Number.isFinite(p.z);

/** Push a sphere out of an AABB. Returns penetration depth, writes the normal. */
function resolveSphereBox(c, r, box, outNormal) {
  const qx = clamp(c.x, box.min.x, box.max.x);
  const qy = clamp(c.y, box.min.y, box.max.y);
  const qz = clamp(c.z, box.min.z, box.max.z);
  const dx = c.x - qx, dy = c.y - qy, dz = c.z - qz;
  const d2 = dx * dx + dy * dy + dz * dz;
  if (d2 > r * r) return 0;
  if (d2 > 1e-12) {
    const d = Math.sqrt(d2);
    outNormal.set(dx / d, dy / d, dz / d);
    return r - d;
  }
  // Centre is inside the box — escape along the least-penetrating axis.
  const pxMin = c.x - box.min.x, pxMax = box.max.x - c.x;
  const pyMin = c.y - box.min.y, pyMax = box.max.y - c.y;
  const pzMin = c.z - box.min.z, pzMax = box.max.z - c.z;
  const px = Math.min(pxMin, pxMax), py = Math.min(pyMin, pyMax), pz = Math.min(pzMin, pzMax);
  if (px <= py && px <= pz) { outNormal.set(pxMin < pxMax ? -1 : 1, 0, 0); return px + r; }
  if (py <= pz) { outNormal.set(0, pyMin < pyMax ? -1 : 1, 0); return py + r; }
  outNormal.set(0, 0, pzMin < pzMax ? -1 : 1);
  return pz + r;
}

/** Slab ray/AABB. Returns t or -1; writes the face normal. */
function rayBox(ox, oy, oz, dx, dy, dz, box, outNormal) {
  let tmin = -Infinity, tmax = Infinity;
  let nAxis = 0, nSign = 0;
  const o = [ox, oy, oz], d = [dx, dy, dz];
  const mn = [box.min.x, box.min.y, box.min.z];
  const mx = [box.max.x, box.max.y, box.max.z];
  for (let a = 0; a < 3; a++) {
    if (Math.abs(d[a]) < 1e-9) {
      if (o[a] < mn[a] || o[a] > mx[a]) return -1;
      continue;
    }
    const inv = 1 / d[a];
    let t1 = (mn[a] - o[a]) * inv;
    let t2 = (mx[a] - o[a]) * inv;
    let s = -1;
    if (t1 > t2) { const t = t1; t1 = t2; t2 = t; s = 1; }
    if (t1 > tmin) { tmin = t1; nAxis = a; nSign = s; }
    if (t2 < tmax) tmax = t2;
    if (tmin > tmax) return -1;
  }
  if (tmax < 0) return -1;
  const t = tmin >= 0 ? tmin : 0;
  outNormal.set(0, 0, 0);
  outNormal.setComponent(nAxis, nSign);
  if (tmin < 0) outNormal.set(0, 1, 0); // started inside
  return t;
}

export class PhysicsAdapter {
  constructor() {
    this.ctx = null;
    this.usingFallback = true;
    this.reason = 'not-initialised';
    this.strikes = 0;
    this.colliders = [];
    this.bounds = new THREE.Box3(new THREE.Vector3(-1e3, -1e3, -1e3), new THREE.Vector3(1e3, 1e3, 1e3));
    this._sig = -1;
    this._sinceRebuild = 1e9;
    this._out = {
      position: new THREE.Vector3(),
      grounded: false,
      groundNormal: new THREE.Vector3(0, 1, 0),
      hitWall: false,
      steppedUp: 0,
    };
    this._hit = { point: new THREE.Vector3(), normal: new THREE.Vector3(0, 1, 0), distance: 0, object: null, surface: 'concrete' };
    this._depenRes = { wall: false, floor: false, ceiling: false, floorNormal: new THREE.Vector3(0, 1, 0) };
    this._ground = { y: 0, normal: new THREE.Vector3(0, 1, 0) };
    this._lastRootCount = -1;
    this.lastCeiling = false;
    this.extra = [];
  }

  /** Register/unregister transient collision geometry (debug + tests only). */
  addExtra(obj) { if (obj && !this.extra.includes(obj)) { this.extra.push(obj); this.rebuild(); } }
  removeExtra(obj) {
    const i = this.extra.indexOf(obj);
    if (i >= 0) { this.extra.splice(i, 1); this.rebuild(); }
  }

  init(ctx) {
    this.ctx = ctx;
    this.rebuild();
    this._probe();
  }

  /* ------------------------------------------------------------ colliders */

  /** Rebuild the local AABB cache from the level. Cheap; called rarely. */
  rebuild() {
    const ctx = this.ctx;
    const list = [];
    const seen = new Set();
    let skipped = 0;
    const roots = [];
    const lvl = ctx?.level;
    if (lvl?.collision?.length) roots.push(...lvl.collision);
    else if (lvl?.root) roots.push(lvl.root);
    else if (ctx?.scene) roots.push(ctx.scene);
    /* Extra colliders registered by the debug suite (test staircases etc.) —
       always empty during normal play. */
    if (this.extra.length) roots.push(...this.extra);

    for (const r of roots) {
      if (!r || !r.traverse) continue;
      r.traverse((o) => {
        if (!o.isMesh || seen.has(o.id)) return;
        seen.add(o.id);
        if (o.userData?.noCollide || o.userData?.isViewmodel) return;
        const nm = (o.name || '').toLowerCase();
        if (nm.includes('sky') || nm.includes('viewmodel') || nm.includes('decal')) return;
        if (!o.geometry) return;
        o.updateWorldMatrix(true, false);
        _box.setFromObject(o, true);
        if (_box.isEmpty() || !finite3(_box.min) || !finite3(_box.max)) return;
        _box.getSize(_size);
        const vol = _size.x * _size.y * _size.z;
        // Reject absurd volumes (sky domes, gizmos) — they'd swallow the world.
        if (vol > 4e5) return;
        if (_size.x < 1e-4 && _size.y < 1e-4 && _size.z < 1e-4) return;
        // An AABB is only a faithful proxy for a box-ish mesh. A merged level
        // collision proxy (thousands of triangles spread over the whole map)
        // would become one solid block and trap the player inside the world,
        // so it is never approximated — the real ctx.physics owns those.
        const verts = o.geometry.attributes?.position?.count ?? 0;
        if (verts > 48 && vol > 60) { skipped++; return; }
        list.push({
          box: _box.clone(),
          surface: o.userData?.surface || 'concrete',
          object: o,
        });
      });
    }
    this.colliders = list;
    this.skippedColliders = skipped;

    /* World bounds come from the level, not from our (filtered) collider cache
       — they drive the kill plane and the spawn search. */
    this.bounds.makeEmpty();
    if (lvl?.navBounds && !lvl.navBounds.isEmpty()) this.bounds.copy(lvl.navBounds);
    for (const r of roots) {
      if (!r?.traverse) continue;
      _box.setFromObject(r, true);
      if (!_box.isEmpty() && finite3(_box.min) && finite3(_box.max)) {
        _box.getSize(_size);
        if (_size.x * _size.y * _size.z < 4e7) this.bounds.union(_box);
      }
    }
    if (this.bounds.isEmpty()) this.bounds.set(new THREE.Vector3(-100, -50, -100), new THREE.Vector3(100, 100, 100));
    this._sig = list.length;
    this._sinceRebuild = 0;
    return list.length;
  }

  maybeRebuild(dt) {
    this._sinceRebuild += dt;
    if (this._sinceRebuild < 1.0) return;
    const lvl = this.ctx?.level;
    const n = lvl?.collision?.length ? lvl.collision.length : (lvl?.root?.children?.length ?? 0);
    if (n !== this._lastRootCount) {
      this._lastRootCount = n;
      this.rebuild();
    } else {
      this._sinceRebuild = 0;
    }
  }

  /* ------------------------------------------------------------- probing */

  /**
   * Decide whether to drive `ctx.physics` or the local fallback.
   *
   * The naivety test is only meaningful against a surface the *external*
   * implementation itself reports. If its own raycast finds a floor and its
   * capsuleMove drops straight through it, it is inconsistent and we take
   * over. If it reports no geometry at all there is nothing to fall through,
   * so we trust it — our AABB cache is a far worse world model than an empty
   * BVH, and the level's collision may simply not be built yet.
   */
  _probe() {
    const p = this.ctx?.physics;
    this.usingFallback = true;
    if (!p || typeof p.capsuleMove !== 'function') {
      this.reason = p ? 'no-capsuleMove' : 'no-physics';
      return;
    }
    const half = 0.9, rad = 0.32;
    const delta = new THREE.Vector3(0, -6, 0);
    try {
      const surf = this._findExternalSurface();
      if (!surf) {
        /* No world registered anywhere. Trust ctx.physics, but only if it at
           least answers with the right shape. */
        const from = _v3.set(0, this.bounds.max.y + 4 + half, 0);
        const res = p.capsuleMove(from, half, rad, delta, { stepHeight: 0.45 });
        if (!res || !res.position || !finite3(res.position) || typeof res.grounded !== 'boolean') {
          this.reason = 'bad-return-shape'; return;
        }
        this.usingFallback = false;
        this.reason = 'ok (no collision geometry registered yet — untested)';
        return;
      }

      const from = _v3.set(surf.x, surf.y + 2 + half, surf.z);
      const res = p.capsuleMove(from, half, rad, delta, { stepHeight: 0.45 });
      if (!res || !res.position || !finite3(res.position)) { this.reason = 'bad-return-shape'; return; }
      if (typeof res.grounded !== 'boolean') { this.reason = 'no-grounded-flag'; return; }
      if (res.position.y <= from.y + delta.y + 0.05) { this.reason = 'naive-no-collision'; return; }
      this.usingFallback = false;
      this.reason = 'ok';
    } catch (err) {
      this.reason = 'threw: ' + String(err?.message || err);
      this.usingFallback = true;
    }
  }

  /** Find any point on the external world using ctx.physics' own raycast. */
  _findExternalSurface() {
    const p = this.ctx?.physics;
    if (typeof p?.raycast !== 'function') return null;
    const b = this.bounds;
    const top = b.max.y + 5;
    const far = (b.max.y - b.min.y) + 60;
    const down = _v2.set(0, -1, 0);
    const sx = b.min.x, sz = b.min.z;
    const dx = Math.max(1, (b.max.x - b.min.x) / 12);
    const dz = Math.max(1, (b.max.z - b.min.z) / 12);
    for (let i = 0; i <= 12; i++) {
      for (let j = 0; j <= 12; j++) {
        _v.set(sx + i * dx, top, sz + j * dz);
        let h = null;
        try { h = p.raycast(_v, down, far, p.MASK?.WORLD); } catch { return null; }
        const n = this._normalise(h, _v, down);
        if (n && n !== undefined && n.point) {
          return { x: n.point.x, y: n.point.y, z: n.point.z };
        }
      }
    }
    return null;
  }

  /** True when *something* can be collided with — either backend. */
  get hasCollision() {
    if (this.colliders.length) return true;
    if (this.usingFallback) return false;
    return !!this._findExternalSurface();
  }

  _strike(why) {
    this.strikes++;
    if (this.strikes >= 3 && !this.usingFallback) {
      this.usingFallback = true;
      this.reason = 'runtime-fallback: ' + why;
    }
  }

  /* ------------------------------------------------------------ raycasts */

  /** Normalised raycast. Always returns the contract shape or null. */
  raycast(origin, dir, far = 200, mask) {
    const p = this.ctx?.physics;
    if (!this.usingFallback && typeof p?.raycast === 'function') {
      try {
        const h = p.raycast(origin, dir, far, mask);
        const n = this._normalise(h, origin, dir);
        if (n !== undefined) return n;
      } catch { this._strike('raycast'); }
    } else if (typeof p?.raycast === 'function' && this.colliders.length === 0) {
      try {
        const h = p.raycast(origin, dir, far, mask);
        const n = this._normalise(h, origin, dir);
        if (n !== undefined) return n;
      } catch { /* fall through */ }
    }
    return this.localRaycast(origin, dir, far);
  }

  /** Convert whatever `physics.raycast` returned into the contract shape. */
  _normalise(h, origin, dir) {
    if (h === null || h === undefined) return null;
    if (!h.point) return undefined; // unrecognised — caller falls back
    const out = this._hit;
    out.point.copy(h.point);
    out.object = h.object || null;
    out.distance = Number.isFinite(h.distance) ? h.distance : out.point.distanceTo(origin);
    if (h.normal) {
      out.normal.copy(h.normal);
    } else if (h.face?.normal && h.object) {
      out.normal.copy(h.face.normal).applyMatrix3(_mat3.getNormalMatrix(h.object.matrixWorld)).normalize();
    } else {
      out.normal.copy(dir).multiplyScalar(-1);
    }
    if (out.normal.dot(dir) > 0) out.normal.multiplyScalar(-1);
    out.surface = h.surface || h.object?.userData?.surface || 'concrete';
    return out;
  }

  /** Local AABB raycast. Same output shape. */
  localRaycast(origin, dir, far = 200) {
    let best = Infinity;
    let bestC = null;
    const dx = dir.x, dy = dir.y, dz = dir.z;
    for (let i = 0; i < this.colliders.length; i++) {
      const c = this.colliders[i];
      const t = rayBox(origin.x, origin.y, origin.z, dx, dy, dz, c.box, _n);
      if (t >= 0 && t < best && t <= far) { best = t; bestC = c; _v3.copy(_n); }
    }
    if (!bestC) return null;
    const out = this._hit;
    out.distance = best;
    out.point.set(origin.x + dx * best, origin.y + dy * best, origin.z + dz * best);
    out.normal.copy(_v3);
    if (out.normal.dot(dir) > 0) out.normal.multiplyScalar(-1);
    out.object = bestC.object;
    out.surface = bestC.surface;
    return out;
  }

  /* -------------------------------------------------------- capsule move */

  /**
   * Move a capsule. `pos` is the capsule CENTRE. Returns a reused object:
   * `{ position, grounded, groundNormal, hitWall, steppedUp }`.
   */
  capsuleMove(pos, halfHeight, radius, delta, opts = {}) {
    const p = this.ctx?.physics;
    if (!this.usingFallback && typeof p?.capsuleMove === 'function') {
      try {
        const r = p.capsuleMove(pos, halfHeight, radius, delta, opts);
        if (r && r.position && finite3(r.position)) {
          const out = this._out;
          this.lastCeiling = false;
          out.position.copy(r.position);
          out.grounded = !!r.grounded;
          out.groundNormal.copy(r.groundNormal || _n.set(0, 1, 0));
          if (out.groundNormal.lengthSq() < 0.1) out.groundNormal.set(0, 1, 0);
          out.hitWall = !!r.hitWall;
          out.steppedUp = Number.isFinite(r.steppedUp) ? r.steppedUp : 0;
          return out;
        }
        this._strike('capsuleMove-shape');
      } catch { this._strike('capsuleMove-throw'); }
    }
    return this.localCapsuleMove(pos, halfHeight, radius, delta, opts);
  }

  localCapsuleMove(pos, halfHeight, radius, delta, opts = {}) {
    const out = this._out;
    const stepHeight = opts.stepHeight ?? 0;
    const wasGrounded = !!opts.wasGrounded;
    const snap = opts.groundSnap ?? 0;

    const p = _v.copy(pos);
    out.hitWall = false;
    out.steppedUp = 0;
    out.grounded = false;
    out.groundNormal.set(0, 1, 0);

    if (!this.colliders.length) {
      out.position.copy(p).add(delta);
      return out;
    }

    const len = delta.length();
    const sub = Math.max(1, Math.min(16, Math.ceil(len / (radius * 0.55))));
    const stepVec = _v2.copy(delta).divideScalar(sub);

    let wall = false;
    let floorHit = false;
    let ceiling = false;
    const floorN = _v3.set(0, 1, 0);

    const wantXZ = Math.hypot(stepVec.x, stepVec.z);
    const dirX = wantXZ > 1e-6 ? stepVec.x / wantXZ : 0;
    const dirZ = wantXZ > 1e-6 ? stepVec.z / wantXZ : 0;

    for (let i = 0; i < sub; i++) {
      const bx = p.x, by = p.y, bz = p.z;
      p.add(stepVec);
      const r = this._depenetrate(p, halfHeight, radius);
      if (r.ceiling) ceiling = true;
      if (r.wall) wall = true;
      if (r.floor) { floorHit = true; floorN.copy(r.floorNormal); }

      /* Step-up. Triggered by a shortfall in the ADVANCE along the intended
         direction, not by a contact-normal classification — a low riser hits
         the bottom cap and reads as a floor normal, which would otherwise
         stall us against every staircase in the game.
         The trial raises the capsule by stepHeight, probes a full radius
         forward for a tread, and — if it finds one — lifts the capsule onto
         that tread at its ORIGINAL xz before re-applying this substep. Lifting
         first is what lets the next substeps glide onto the step instead of
         grinding against the riser one skin-width at a time. */
      if (stepHeight > 0 && wantXZ > 1e-5 && (wasGrounded || floorHit)) {
        const advance = (p.x - bx) * dirX + (p.z - bz) * dirZ;
        if (advance < wantXZ * 0.85) {
          const sx = p.x, sy = p.y, sz = p.z;
          const feetY = by - halfHeight;
          let ok = false;
          p.set(bx, by + stepHeight, bz);
          if (!this.localOverlap(p, halfHeight, radius)) {
            const reach = radius + 0.10;
            p.x = bx + dirX * reach;
            p.z = bz + dirZ * reach;
            if (!this.localOverlap(p, halfHeight, radius)) {
              const g = this._groundProbe(p, halfHeight, radius, stepHeight + 0.10);
              if (g && g.y > feetY + 0.01 && g.y <= feetY + stepHeight + 1e-3) {
                p.x = bx; p.z = bz;
                p.y = g.y + halfHeight;
                this._depenetrate(p, halfHeight, radius);
                out.steppedUp = Math.max(out.steppedUp, p.y - by);
                floorHit = true; floorN.copy(g.normal);
                p.x += stepVec.x; p.z += stepVec.z;
                const r2 = this._depenetrate(p, halfHeight, radius);
                if (r2.wall) wall = true;
                if (r2.floor) floorN.copy(r2.floorNormal);
                ok = true;
              }
            }
          }
          if (!ok) { p.set(sx, sy, sz); wall = true; }
        }
      }
    }

    // Ground probe / snap. Skip while ascending — otherwise a jump is eaten
    // on its first step, when the capsule is still inside the probe range.
    const ascending = delta.y > 1e-4;
    const g = ascending ? null : this._groundProbe(p, halfHeight, radius, opts.groundProbe ?? 0.14);
    if (g) {
      out.grounded = true;
      out.groundNormal.copy(g.normal);
      p.y = g.y + halfHeight;
    } else if (floorHit && !ascending) {
      out.grounded = true;
      out.groundNormal.copy(floorN);
    } else if (wasGrounded && snap > 0 && delta.y <= 1e-4) {
      const s = this._groundProbe(p, halfHeight, radius, snap);
      if (s) {
        p.y = s.y + halfHeight;
        out.grounded = true;
        out.groundNormal.copy(s.normal);
      }
    }

    this.lastCeiling = ceiling;
    out.hitWall = wall;
    out.position.copy(p);
    return out;
  }

  /** Push the capsule out of everything it overlaps. */
  _depenetrate(p, halfHeight, radius) {
    const segHalf = Math.max(0, halfHeight - radius);
    const res = this._depenRes;
    res.wall = false; res.floor = false; res.ceiling = false;
    let bestFy = -1;
    res.floorNormal.set(0, 1, 0);
    for (let iter = 0; iter < 4; iter++) {
      let moved = false;
      for (let i = 0; i < this.colliders.length; i++) {
        const b = this.colliders[i].box;
        if (p.x + radius < b.min.x || p.x - radius > b.max.x) continue;
        if (p.z + radius < b.min.z || p.z - radius > b.max.z) continue;
        if (p.y + halfHeight < b.min.y || p.y - halfHeight > b.max.y) continue;
        const cy = clamp((b.min.y + b.max.y) * 0.5, p.y - segHalf, p.y + segHalf);
        _sphere.set(p.x, cy, p.z);
        const depth = resolveSphereBox(_sphere, radius, b, _push);
        if (depth > 1e-6) {
          p.addScaledVector(_push, depth + 1e-5);
          moved = true;
          if (_push.y > 0.5) {
            res.floor = true;
            if (_push.y > bestFy) { bestFy = _push.y; res.floorNormal.copy(_push); }
          } else if (_push.y < -0.5) {
            res.ceiling = true;
          } else {
            res.wall = true;
          }
        }
      }
      if (!moved) break;
    }
    return res;
  }

  /**
   * Probe for ground under the capsule. Uses five rays (centre + 4 rim) so we
   * do not fall off the edge of a step. Returns `{y, normal}` or null.
   */
  _groundProbe(p, halfHeight, radius, dist) {
    const top = p.y - halfHeight + 0.06;
    const r = radius * 0.7;
    const offs = [[0, 0], [r, 0], [-r, 0], [0, r], [0, -r]];
    let bestY = -Infinity;
    let found = false;
    for (let i = 0; i < offs.length; i++) {
      const ox = p.x + offs[i][0], oz = p.z + offs[i][1];
      for (let j = 0; j < this.colliders.length; j++) {
        const c = this.colliders[j];
        const t = rayBox(ox, top, oz, 0, -1, 0, c.box, _n);
        if (t >= 0 && t <= dist + 0.06) {
          const y = top - t;
          if (y > bestY) { bestY = y; found = true; _gn.copy(_n); }
        }
      }
    }
    if (!found) return null;
    const out = this._ground;
    out.y = bestY;
    out.normal.copy(_gn);
    if (out.normal.y < 0.2) out.normal.set(0, 1, 0);
    return out;
  }

  /* ------------------------------------------------------------- overlap */

  overlapCapsule(pos, halfHeight, radius) {
    const p = this.ctx?.physics;
    if (!this.usingFallback && typeof p?.overlapCapsule === 'function') {
      try {
        const r = p.overlapCapsule(pos, halfHeight, radius);
        if (typeof r === 'boolean') return r;
      } catch { this._strike('overlapCapsule'); }
    }
    return this.localOverlap(pos, halfHeight, radius);
  }

  /**
   * Does a capsule standing with its feet at `feet` fit?
   *
   * The capsule is shrunk by `skin` before the test. Without that, a capsule
   * resting exactly on the floor reads as overlapping in any implementation
   * that treats touching as intersecting — which would make it impossible to
   * ever stand up, mantle, or find a spawn.
   */
  capsuleFits(feet, height, radius, skin = 0.05) {
    /* Head-room by raycast: centre plus four rim rays. Raycasts are the one
       query every physics backend agrees on, and unlike an overlap test they
       cannot be fooled by a capsule that is merely *resting* on the floor. */
    const far = Math.max(0.05, height - skin * 2);
    const r = radius * 0.72;
    for (let i = 0; i < 5; i++) {
      const ox = i === 1 ? r : i === 2 ? -r : 0;
      const oz = i === 3 ? r : i === 4 ? -r : 0;
      _v.set(feet.x + ox, feet.y + skin, feet.z + oz);
      if (this.raycast(_v, _up, far)) return false;
    }
    /* Our own boxes are exact, so use them for the lateral case too. */
    if (this.usingFallback && this.colliders.length) {
      const h = Math.max(0.06, height - skin * 2);
      _v.set(feet.x, feet.y + skin + h * 0.5, feet.z);
      if (this.localOverlap(_v, h * 0.5, Math.max(0.05, radius - skin * 0.5), 0.01)) return false;
    }
    return true;
  }

  localOverlap(pos, halfHeight, radius, tolerance = 0.004) {
    const segHalf = Math.max(0, halfHeight - radius);
    for (let i = 0; i < this.colliders.length; i++) {
      const b = this.colliders[i].box;
      if (pos.x + radius < b.min.x || pos.x - radius > b.max.x) continue;
      if (pos.z + radius < b.min.z || pos.z - radius > b.max.z) continue;
      if (pos.y + halfHeight < b.min.y || pos.y - halfHeight > b.max.y) continue;
      const cy = clamp((b.min.y + b.max.y) * 0.5, pos.y - segHalf, pos.y + segHalf);
      _sphere.set(pos.x, cy, pos.z);
      if (resolveSphereBox(_sphere, radius, b, _n) > tolerance) return true;
    }
    return false;
  }

  /** How deep the capsule is inside geometry (0 = clear). Local, always. */
  penetration(pos, halfHeight, radius) {
    const segHalf = Math.max(0, halfHeight - radius);
    let worst = 0;
    for (let i = 0; i < this.colliders.length; i++) {
      const b = this.colliders[i].box;
      if (pos.x + radius < b.min.x || pos.x - radius > b.max.x) continue;
      if (pos.z + radius < b.min.z || pos.z - radius > b.max.z) continue;
      if (pos.y + halfHeight < b.min.y || pos.y - halfHeight > b.max.y) continue;
      const cy = clamp((b.min.y + b.max.y) * 0.5, pos.y - segHalf, pos.y + segHalf);
      _sphere.set(pos.x, cy, pos.z);
      worst = Math.max(worst, resolveSphereBox(_sphere, radius, b, _n));
    }
    return worst;
  }

  surfaceAt(point, fallback = 'concrete') {
    const lvl = this.ctx?.level;
    if (typeof lvl?.surfaceAt === 'function') {
      try {
        const s = lvl.surfaceAt(point);
        if (typeof s === 'string' && s) return s;
      } catch { /* ignore */ }
    }
    return fallback;
  }

  get diagnostics() {
    return {
      usingFallback: this.usingFallback,
      reason: this.reason,
      strikes: this.strikes,
      colliders: this.colliders.length,
      skippedColliders: this.skippedColliders ?? 0,
      bounds: [this.bounds.min.toArray(), this.bounds.max.toArray()],
    };
  }
}

export default PhysicsAdapter;
